import React, { useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, ImageIcon, MailIcon, PhoneIcon, Trash2Icon, XIcon } from 'lucide-react';
import { Button, LinkButton } from '../../components/ui/Button';
import { SelectField, TextField } from '../../components/ui/Field';
import { StatusBadge } from '../../components/admin/StatusBadge';
import { StatusTimeline } from '../../components/admin/StatusTimeline';
import { useAdminData } from '../../contexts/AdminDataContext';
import { caseStatuses, photoStages } from '../../types/admin';
import type { CaseStatus, PhotoStage } from '../../types/admin';
import { formatDate, formatDateTime, formatPrice } from '../../utils/format';
const ease = [0.23, 1, 0.32, 1] as const;
export function CaseDetail() {
  const {
    caseId
  } = useParams();
  const navigate = useNavigate();
  const {
    cases,
    customerById,
    setStatus,
    addNote,
    deleteNote,
    addPhotos,
    deletePhoto,
    updateCase,
    deleteCase
  } = useAdminData();
  const item = cases.find(entry => entry.id === caseId);
  const [note, setNote] = useState('');
  const [stage, setStage] = useState<PhotoStage>('Før');
  if (!item) return <Navigate to="/admin/sager" replace data-id="element-197" data-mp="2or4elzmpbuy0" />;
  const customer = customerById(item.customerId);
  return <div className="px-5 py-7 md:px-8 md:py-10" data-id="element-198" data-mp="2jovnl8te32yj">
      <Link to="/admin/sager" className="inline-flex items-center gap-2 text-sm text-ink-muted transition-colors duration-200 ease-smooth hover:text-ink" data-id="element-199" data-mp="2xppv7p8m4zw5">
        <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-200" data-mp="37g1bmb9hh1jx" />
        Sager
      </Link>

      <header className="mt-4 flex flex-wrap items-start justify-between gap-4" data-id="element-201" data-mp="37hvmewr1jdaa">
        <div data-id="element-202" data-mp="32hpztuzmogz2">
          <p className="text-sm text-ink-muted" data-id="element-203" data-mp="32jajuqobghio">Sag #{item.number}</p>
          <h1 className="mt-1 font-display text-[clamp(1.7rem,4.5vw,2.4rem)] font-bold leading-[1.05] tracking-tight text-ink" data-id="element-204" data-mp="3gh8w9qj2u3vj">
            {item.title}
          </h1>
          <p className="mt-1.5 text-[15px] text-ink-soft" data-id="element-205" data-mp="3jj3mc1zz9ks0">
            {customer?.name}
            {item.fromWebsite && <span className="ml-2 rounded-full bg-canvas-deep px-2 py-0.5 text-xs text-ink-muted" data-id="element-206" data-mp="3oi6q7j481kye">
                Fra hjemmesiden
              </span>}
          </p>
        </div>
        <div className="mt-2 flex items-center gap-3" data-id="element-207" data-mp="22m49rxfw1ey">
          <StatusBadge status={item.status} data-id="element-208" data-mp="63foobboay33" />
          <button type="button" onClick={() => {
          if (window.confirm(`Slet sag #${item.number}? Handlingen kan ikke fortrydes.`)) {
            deleteCase(item.id);
            navigate('/admin/sager');
          }
        }} className="inline-flex h-9 items-center gap-2 rounded-full border border-line px-3.5 text-sm text-ink-soft transition-colors duration-200 ease-smooth hover:border-ink/30 hover:text-ink" data-id="element-209" data-mp="62mhj98sy6kf">
            <Trash2Icon className="h-4 w-4" aria-hidden="true" data-id="element-210" data-mp="2mt2zi8j65edc" />
            Slet sag
          </button>
        </div>
      </header>

      <section className="mt-6 rounded-card border border-line bg-white p-5 md:p-6" data-id="element-211" data-mp="13y8dmiw4whz2">
        <div className="flex flex-wrap items-end justify-between gap-4" data-id="element-212" data-mp="z4nnfd0s8saf">
          <SelectField id="sag-status" label="Status" className="w-full sm:w-64" value={item.status} onChange={event => setStatus(item.id, event.target.value as CaseStatus)} options={caseStatuses.map(option => ({
          value: option,
          label: option
        }))} data-id="element-213" data-mp="v5ok1wup93l3" />
        </div>
        <div className="mt-6" data-id="element-214" data-mp="1is3htblchj3x">
          <StatusTimeline status={item.status} data-id="element-215" data-mp="1dulec0q3jw3b" />
        </div>
      </section>

      <div className="mt-6 grid gap-5 lg:grid-cols-[1.4fr_0.6fr]" data-id="element-216" data-mp="18w0e41auhcev">
        <div className="space-y-5" data-id="element-217" data-mp="18vs0262zk123">
          <section className="rounded-card border border-line bg-white p-6" data-id="element-218" data-mp="2fy0h8aat9hl">
            <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-219" data-mp="2dv3u5en4i3w">Opgave</h2>
            <p className="mt-3 text-[16px] leading-relaxed text-ink-soft" data-id="element-220" data-mp="3tobxe5356xlk">
              {item.description || 'Ingen beskrivelse endnu.'}
            </p>
            <dl className="mt-5 grid gap-4 border-t border-line pt-5 sm:grid-cols-3" data-id="element-221" data-mp="1q4j3z584bp5v">
              <div data-id="element-222" data-mp="1cdecwritg575">
                <dt className="text-xs uppercase tracking-wider text-ink-muted" data-id="element-223" data-mp="1cdxkbeaubb6x">Kategori</dt>
                <dd className="mt-1 text-[15px] text-ink" data-id="element-224" data-mp="1hd0qx36gxph0">{item.category}</dd>
              </div>
              <div data-id="element-225" data-mp="251a4jhtj36x2">
                <dt className="text-xs uppercase tracking-wider text-ink-muted" data-id="element-226" data-mp="2521sq7ksq2db">Adresse</dt>
                <dd className="mt-1 text-[15px] text-ink" data-id="element-227" data-mp="291u5yxl4wwp0">{item.address || '—'}</dd>
              </div>
              <div data-id="element-228" data-mp="1v2krc2ckuan5">
                <dt className="text-xs uppercase tracking-wider text-ink-muted" data-id="element-229" data-mp="1v5gmjix1il7w">Planlagt dato</dt>
                <dd className="mt-1 text-[15px] text-ink" data-id="element-230" data-mp="200ma0m63c7y3">{formatDate(item.plannedDate)}</dd>
              </div>
            </dl>
            <TextField id="sag-dato" label="Ret planlagt dato" type="date" className="mt-5 max-w-xs" value={item.plannedDate} onChange={event => updateCase(item.id, {
            plannedDate: event.target.value
          })} data-id="element-231" data-mp="ona7qfpzbxe1" />
          </section>

          <section className="rounded-card border border-line bg-white p-6" data-id="element-232" data-mp="q4pf3oy94d5k">
            <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-233" data-mp="l5m8w16jc0w0">Noter</h2>
            <form className="mt-4 flex flex-col gap-3 sm:flex-row" onSubmit={event => {
            event.preventDefault();
            if (!note.trim()) return;
            addNote(item.id, note.trim());
            setNote('');
          }} data-id="element-234" data-mp="3tl5zstjqh77w">
              <label htmlFor="ny-note" className="sr-only" data-id="element-235" data-mp="2f4vr4y6rdvr">
                Ny note
              </label>
              <input id="ny-note" value={note} onChange={event => setNote(event.target.value)} placeholder="Fx: kunden ønsker arbejdet udført efter kl. 15." className="flex-1 rounded-xl border border-line bg-white px-4 py-3 text-[15px] text-ink placeholder:text-ink-faint transition-colors duration-200 ease-smooth focus:border-accent focus:outline-none" data-id="element-236" data-mp="3aueuq46s42zb" />
              <Button type="submit" variant="dark" data-id="element-237" data-mp="1jj1vt4mdy2i2">
                Tilføj note
              </Button>
            </form>
            <ul className="mt-5 space-y-3" data-id="element-238" data-mp="1oj63c55bfmw5">
              {item.internalNotes.map(entry => <motion.li key={entry.id} initial={{
              opacity: 0,
              y: 6
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.22,
              ease
            }} className="flex items-start justify-between gap-3 rounded-xl bg-canvas p-4" data-id="element-239" data-mp="qvxlql8fqxzf">
                  <div data-id="element-240" data-mp="2c7gwsn8omkae">
                    <p className="text-xs text-ink-muted" data-id="element-241" data-mp="2c5w4735xhbf6">{formatDateTime(entry.createdAt)}</p>
                    <p className="mt-1 text-[15px] leading-relaxed text-ink" data-id="element-242" data-mp="2g7hnb6922lxt">{entry.text}</p>
                  </div>
                  <button type="button" onClick={() => deleteNote(item.id, entry.id)} aria-label="Slet note" className="shrink-0 rounded-full p-1.5 text-ink-faint transition-colors duration-200 ease-smooth hover:bg-white hover:text-ink" data-id="element-243" data-mp="4jw26g3c8gpn">
                    <Trash2Icon className="h-4 w-4" aria-hidden="true" data-id="element-244" data-mp="3mxc6k6wy4u2t" />
                  </button>
                </motion.li>)}
              {item.internalNotes.length === 0 && <li className="text-sm text-ink-muted" data-id="element-245" data-mp="3d3butm75sql4">Ingen noter endnu.</li>}
            </ul>
          </section>

          <section className="rounded-card border border-line bg-white p-6" data-id="element-246" data-mp="340u876nldvxl">
            <div className="flex flex-wrap items-end justify-between gap-4" data-id="element-247" data-mp="1780ryprlrdxl">
              <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-248" data-mp="175y4odhu3ywk">Billeder</h2>
              <div className="flex items-center gap-2" data-id="element-249" data-mp="126uygpq4bmn0">
                <div className="flex gap-1.5" role="group" aria-label="Vælg kategori for upload" data-id="element-250" data-mp="b4mwrjefzfgm">
                  {photoStages.map(option => <button key={option} type="button" onClick={() => setStage(option)} aria-pressed={stage === option} className={`rounded-full border px-3 py-1.5 text-xs transition-colors duration-200 ease-smooth ${stage === option ? 'border-ink bg-ink text-white' : 'border-line bg-white text-ink-soft hover:border-ink/30'}`} data-id="element-251" data-mp="g4ho1o24q727">
                      {option}
                    </button>)}
                </div>
                <label htmlFor="sag-billeder" className="inline-flex h-9 cursor-pointer items-center gap-2 rounded-full border border-line px-3.5 text-sm text-ink transition-colors duration-200 ease-smooth hover:border-ink/30" data-id="element-252" data-mp="1cu8saf0838l7">
                  <ImageIcon className="h-4 w-4" aria-hidden="true" data-id="element-253" data-mp="2xbrhf17rr8yq" />
                  Upload
                  <input id="sag-billeder" type="file" multiple accept="image/*" className="sr-only" onChange={event => {
                  const files = Array.from(event.target.files ?? []).map(file => ({
                    url: URL.createObjectURL(file),
                    name: file.name
                  }));
                  if (files.length) addPhotos(item.id, files, stage);
                }} data-id="element-254" data-mp="3c04drzarqttr" />
                </label>
              </div>
            </div>

            <div className="mt-5 space-y-6" data-id="element-255" data-mp="20l8yqc87mqco">
              {photoStages.map(group => {
              const photos = item.photos.filter(photo => photo.stage === group);
              return <div key={group} data-id="element-256" data-mp="2m0novntf6s7l">
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-ink-muted" data-id="element-257" data-mp="2h09i093k1ob5">{group}</h3>
                    {photos.length > 0 ? <ul className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4" data-id="element-258" data-mp="2c1onmqazhyum">
                        {photos.map(photo => <li key={photo.id} className="relative" data-id="element-259" data-mp="34pub3ktfa1y5">
                            <img src={photo.url} alt={`${group} — ${photo.name}`} className="aspect-square w-full rounded-xl object-cover" data-id="element-260" data-mp="34paohqstupds" />
                            <button type="button" onClick={() => deletePhoto(item.id, photo.id)} aria-label={`Slet billede ${photo.name}`} className="absolute right-2 top-2 inline-flex h-7 w-7 items-center justify-center rounded-full bg-white/90 text-ink transition-colors duration-200 ease-smooth hover:bg-white" data-id="element-261" data-mp="2q1h9o8bkfqw9">
                              <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-262" data-mp="3v1nxdm18fv5d" />
                            </button>
                          </li>)}
                      </ul> : <p className="mt-2 text-sm text-ink-muted" data-id="element-263" data-mp="drzeg6x5z7cd">Ingen billeder.</p>}
                  </div>;
            })}
            </div>
          </section>
        </div>

        <aside className="space-y-5" data-id="element-264" data-mp="mjj7ccf4emzf">
          <section className="rounded-card border border-line bg-white p-6" data-id="element-265" data-mp="mkkpf0uk09qn">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-ink-muted" data-id="element-266" data-mp="ri2ugg5ga4td">Kunde</h2>
            <p className="mt-3 font-display text-lg font-bold tracking-tight text-ink" data-id="element-267" data-mp="wja30hff3813">{customer?.name}</p>
            <ul className="mt-3 space-y-1.5 text-[15px] text-ink-soft" data-id="element-268" data-mp="11i3fq7mvqv07">
              <li data-id="element-269" data-mp="bsd4hh6oxpmz">{customer?.phone || '—'}</li>
              <li className="break-all" data-id="element-270" data-mp="btfxbunkq25j">{customer?.email || '—'}</li>
              <li data-id="element-271" data-mp="6v4nhjxa3rnb">{customer?.address || '—'}</li>
            </ul>
            <div className="mt-5 flex flex-col gap-2 sm:flex-row" data-id="element-272" data-mp="lipnv62rrcrj">
              <LinkButton href={`tel:${customer?.phone ?? ''}`} variant="secondary" size="sm" data-id="element-273" data-mp="ll2f69uf2zh9">
                <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-274" data-mp="gq6945scibl5" />
                Ring
              </LinkButton>
              <LinkButton href={`mailto:${customer?.email ?? ''}`} variant="secondary" size="sm" data-id="element-275" data-mp="ulh21dn14mmv">
                <MailIcon className="h-4 w-4" aria-hidden="true" data-id="element-276" data-mp="pmxamxpo2in4" />
                Send e-mail
              </LinkButton>
            </div>
            {customer && <Link to={`/admin/kunder/${customer.id}`} className="mt-4 inline-block text-sm font-medium text-accent hover:text-accent-deep" data-id="element-277" data-mp="zja4bvl7owhv">
                Se kundeprofil
              </Link>}
          </section>

          <section className="rounded-card border border-line bg-white p-6" data-id="element-278" data-mp="1gt4ar7tqtrnq">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-ink-muted" data-id="element-279" data-mp="1lqwb8q8gem3j">Økonomi</h2>
            <dl className="mt-4 divide-y divide-line/70 text-[15px]" data-id="element-280" data-mp="172rs5l5ho8k8">
              <div className="flex justify-between gap-3 py-2.5" data-id="element-281" data-mp="170fc5djak89t">
                <dt className="text-ink-soft" data-id="element-282" data-mp="1bzrylx693dbv">Timepris</dt>
                <dd className="font-medium text-ink" data-id="element-283" data-mp="y1232lz2xlpm">{formatPrice(item.hourlyRate, 'kr./time')}</dd>
              </div>
              <div className="flex justify-between gap-3 py-2.5" data-id="element-284" data-mp="12484ngb8yopr">
                <dt className="text-ink-soft" data-id="element-285" data-mp="o3nluk5c0sw9">Fast tilbud</dt>
                <dd className="font-medium text-ink" data-id="element-286" data-mp="o58h6bzh0fv6">{formatPrice(item.fixedPrice)}</dd>
              </div>
              <div className="flex justify-between gap-3 py-2.5" data-id="element-287" data-mp="t4l22r6s7cv4">
                <dt className="text-ink-soft" data-id="element-288" data-mp="2iigdk833gzix">Anslået beløb</dt>
                <dd className="font-medium text-ink" data-id="element-289" data-mp="2ng9jd00hjrio">{formatPrice(item.estimate)}</dd>
              </div>
            </dl>
            <TextField id="sag-slutbeloeb" label="Endeligt beløb (kr.)" className="mt-4" inputMode="numeric" placeholder="Udfyldes når opgaven er færdig" value={item.finalAmount} onChange={event => updateCase(item.id, {
            finalAmount: event.target.value
          })} data-id="element-290" data-mp="neig52xu9llw" />
          </section>
        </aside>
      </div>
    </div>;
}